<template>
    <vx-modal id="modal-assign-conversation"
        v-model:visible="visible"
        padding="standard"
        size="md">
        <template v-slot:header>{{ title }}</template>

        <div class="vx-flex vx-flex-col vx-gap-2">
            <vx-alert v-if="displayFirstError(validate.assignee_id)" color="danger" :closeable="false">
                Please select an agent.
            </vx-alert>

            <div class="vx-flex vx-flex-col">
                <vx-radio-selection v-model="choice">
                    <vx-radio-selection-item v-for="(option, index) in choices" :key="index" :value="option.key">
                        {{ option.label }}
                    </vx-radio-selection-item>
                </vx-radio-selection>
            </div>

            <vx-selectable
                v-model="agent"
                :clearable="false"
                size="lg"
                relative-dropdown
                label="name"
                :placeholder="selectedAgent || 'Select a person...'"
                :disabled="choice === 'mySelf'"
                :options="agentsOptions">
            </vx-selectable>
        </div>

        <template v-slot:footer>
            <div class="vx-flex vx-flex-col-reverse vx-gap-4 vx-justify-between lg:vx-flex-row">

                <vx-button color="muted"
                    size="lg"
                    @click="visible = false">
                    Cancel
                </vx-button>

                <vx-button class="vx-grow"
                    @click.prevent="submit()"
                    color="primary"
                    data-test="submit-button"
                    size="lg"
                    type="button">
                    Done
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { useStore } from 'vuex';
import {
    VxAlert,
    VxButton,
    VxModal,
    VxRadioSelection,
    VxRadioSelectionItem,
    VxSelectable,
    VxSwal,
} from '@voxie/frontend-components';
import { contactName } from '../../../../filters'
import { computed, onMounted, ref, watch } from 'vue';
import { useToasted } from '~/composables/useToasted';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { displayFirstError } from '~/utils/validation';

const store = useStore();
const toasted = useToasted();

const visible = ref(false);
const choices = [
    {
        key: 'mySelf',
        label: 'Assign to myself'
    },
    {
        key: 'someOneElse',
        label: 'Assign to someone else'
    },
];
const choice = ref('mySelf');
const agent = ref(null);

const validate = useVuelidate({
    assignee_id: { required },
}, computed(() => ({ assignee_id: agent.value?.assignee_id })));

const selectedContact = computed(() => store.state.contacts_v3.selectedContact);
const selectedThreads = computed(() => store.state.threads.selectedThreads);
const agents = computed(() => store.state.threads.agents);
const activeThread = computed(() => store.getters['getActiveThread']);
const assignThread = (...args) => store.dispatch('assignThread', ...args);
const unAssignThread = (...args) => store.dispatch('unAssignThread', ...args);

const contactFullName = computed(() => {
    if (selectedThreads.value.length) return '';

    if (selectedContact.value.id) {
        return contactName(selectedContact.value);
    }

    return '';
});

const title = computed(() => {
    if (selectedThreads.value.length > 1) {
        return 'Assign conversations to';
    }

    if (contactFullName.value) {
        return `Assign the conversation with ${contactFullName.value} to`;
    }

    return 'Assign the conversation to';
});

const agentsOptions = computed(() => {
    const options = [
        {
            assignee_id: 'unassigned',
            name: 'None (Unassigned)',
        },
    ];

    return [
        ...options,
        ...agents.value.map(a => ({
            assignee_id: a.id,
            name: a.name,
        })).filter(a => a.assignee_id !== Spark.state.user.id),
    ];
});

const selectedAgent = computed(() => {
    return agent.value ? agent.value.name : '';
});

const setMySelf = () => {
    agent.value = {
        assignee_id: Spark.state.user.id,
        name: Spark.state.user.name,
    }
};

const submit = async () => {
    const isValid = await validate.value.$validate();

    if (!isValid) {
        return;
    }

    const assigneeId = agent.value.assignee_id;

    let isUnassigned = agent.value.assignee_id === 'unassigned'
    let promises = []
    if (selectedThreads.value.length) {
        selectedThreads.value.forEach((thread) => {
            if (isUnassigned) {
                promises = [...promises, unAssignThread({ thread })]
            } else {
                promises = [...promises, assignThread({ thread, assigneeId: assigneeId })]
            }
        })
    } else if (activeThread.value && activeThread.value.contact_id) {
        if (isUnassigned) {
            promises = [...promises, unAssignThread({ thread: activeThread.value })]
        } else {
            promises = [...promises, assignThread({ thread: activeThread.value, assigneeId: assigneeId })]
        }
    }

    Promise.all(promises).then(() => {
        visible.value = false;

        const textPrefix = selectedThreads.value.length ? 'Threads were' : 'The thread was'
        if (isUnassigned) {
            VxSwal.fire({ title: 'Success', text: `${textPrefix} unassigned successfully.`, icon: 'success' })
        } else {
            VxSwal.fire({ title: 'Success', text: `${textPrefix} assigned successfully.`, icon: 'success' })
        }
    }).catch(e => {
        toasted.global.platform_error();

        throw e;
    });
};

watch(choice, (newValue) => {
    if (newValue === 'mySelf') {
        setMySelf()
    } else {
        agent.value = null
    }
}, { immediate: true });

onMounted(() => {
    Bus.$on('showAssignConversationUi', () => {
        visible.value = true;
    })
});
</script>
